import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BrandMark, Button, Card, Input, ThemeToggle } from '@clari/ui'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ErrorBanner } from '@/components/ErrorBanner'
import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error'
import { authApi } from '@/lib/admin-api'
import { useAuthStore } from '@/stores/authStore'

const OPERATOR_ROLES = ['admin', 'support']

export function Login() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await authApi.login(email, password, twoFactorRequired ? code : undefined)
      if ('two_factor_required' in res) {
        setTwoFactorRequired(true)
        return
      }
      if (!OPERATOR_ROLES.includes(res.user.role)) {
        setError(t('login.not_authorized'))
        return
      }
      setSession({ user: res.user, accessToken: res.access_token, refreshToken: res.refresh_token })
      navigate('/users', { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? t('auth.invalid_credentials', { ns: 'errors' })
          : apiErrorMessage(err),
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg text-text">
      <header className="flex items-center justify-between px-4 py-4 sm:px-6">
        <BrandMark size={22} />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle defaultMode="dark" />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <Card>
            <h1 className="text-2xl font-semibold">{t('login.title')}</h1>
            <p className="mt-1 text-text-muted">{t('login.subtitle')}</p>
            <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit}>
              <Input
                label={t('login.email_label')}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={twoFactorRequired}
              />
              <Input
                label={t('login.password_label')}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={twoFactorRequired}
              />
              {twoFactorRequired ? (
                <Input
                  label={t('login.twofa_code_label')}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              ) : null}
              {error ? <ErrorBanner message={error} /> : null}
              <Button type="submit" disabled={loading}>
                {twoFactorRequired ? t('login.twofa_submit') : t('login.submit')}
              </Button>
            </form>
          </Card>
        </div>
      </main>
    </div>
  )
}
